import { operators as o, common as c } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

export default {
    name: 'PageFeatureAlertsAggregationCount',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.PAGE_FEATURE_SETTINGS,
    responseFormat: 'rows',
    build: ({ appId, createdAt }: { appId: c.AppIdType; createdAt: number }) =>
        o.pipeline(
            o.spawn(
                o.pipeline(
                    o.sources.pages({ appId }),
                    o.filter('dirty == false'),
                    o.evalExpression({
                        pageId: 'id',
                        pageName: 'name',
                    }),
                ),
                o.pipeline(
                    o.sources.features({ appId }),
                    o.filter('dirty == false'),
                    o.evalExpression({
                        featureId: 'id',
                        featureName: 'name',
                    }),
                ),
            ),
            o.merge(
                ['appId'],
                o.mappings({
                    'appId': 'id',
                    'app.appId': 'id',
                    'app.displayName': 'displayName',
                    'app.platform': 'platform',
                }),
                o.pipeline(o.sources.apps(), o.filter('applicationFlags.mobileRetroactiveAnalyticsApp == true')),
            ),
            o.merge(
                ['appId'],
                o.mappings({
                    'app.totalVisitors': 'totalVisitors',
                }),
                o.pipeline(
                    o.sources.events({
                        blacklist: 'apply',
                        appId,
                        timeSeries: {
                            period: 'dayRange',
                            first: 'dateAdd(startOfPeriod("daily",now()),-60,"days")',
                            count: 61,
                        },
                    }),
                    o.group(['appId'], o.groupField('totalVisitors', o.count('visitorId'))),
                ),
            ),
            o.merge(
                ['appId'],
                {},
                o.pipeline(
                    {
                        source: {
                            mobileapplicationversions: {
                                appId,
                                targetEnvironment: 'Production',
                            },
                        },
                    },
                    o.filter(`createdAt > ${createdAt}`),
                    o.evalExpression({
                        'v.appVersion': 'version',
                        'v.appVersionDate': 'formatTime(`2006-01-02 03:04`, createdAt)',
                        'v.appVersionTimestamp': 'createdAt',
                    }),
                    o.group(['appId'], o.groupField('appData', o.list('v'))),
                ),
            ),
            o.evalExpression({
                numFeatureEvents: '0',
                numFeatureVisitors: '0',
                numPageEvents: '0',
                numPageVisitors: '0',
            }),
            o.unwind('appData'),
            o.evalExpression({
                appVersion: 'appData.appVersion',
            }),
            o.merge(
                ['appId', 'pageId', 'appVersion'],
                {},
                o.pipeline(
                    o.sources.pageEvents({
                        blacklist: 'apply',
                        appId,
                        timeSeries: {
                            period: 'dayRange',
                            first: 'dateAdd(startOfPeriod("daily",now()),-60,"days")',
                            count: 61,
                        },
                    }),
                    o.userAgent('agent'),
                    o.evalExpression({
                        appVersion: 'agent.appVersion',
                    }),
                    o.group(
                        ['pageId', 'appId', 'appVersion'],
                        o.groupField('numPageEvents', { sum: 'numEvents' }),
                        o.groupField('numPageVisitors', { count: 'visitorId' }),
                    ),
                ),
            ),
            o.merge(
                ['appId', 'featureId', 'appVersion'],
                {},
                o.pipeline(
                    o.sources.featureEvents({
                        blacklist: 'apply',
                        appId,
                        timeSeries: {
                            period: 'dayRange',
                            first: 'dateAdd(startOfPeriod("daily",now()),-60,"days")',
                            count: 61,
                        },
                    }),
                    o.userAgent('agent'),
                    o.evalExpression({
                        appVersion: 'agent.appVersion',
                    }),
                    o.group(
                        ['featureId', 'appId', 'appVersion'],
                        o.groupField('numFeatureEvents', { sum: 'numEvents' }),
                        o.groupField('numFeatureVisitors', { count: 'visitorId' }),
                    ),
                ),
            ),
            o.evalExpression({
                'r.appVersion': 'appVersion',
                'r.numPageEvents': 'numPageEvents',
                'r.numPageVisitors': 'numPageVisitors',
                'r.numFeatureEvents': 'numFeatureEvents',
                'r.numFeatureVisitors': 'numFeatureVisitors',
                'r.appVersionDate': 'appData.appVersionDate',
                'r.appVersionTimestamp': 'appData.appVersionTimestamp',
                'r.numEvents': 'numPageEvents + numFeatureEvents',
                'r.numVisitors': 'numPageVisitors + numFeatureVisitors',
            }),
            o.merge(
                ['appId', 'pageId', 'featureId'],
                {},
                o.pipeline(
                    o.sources.guides({
                        appId,
                    }),
                    o.filter('state == "public"'),
                    o.unwind('steps'),
                    o.unwind('steps.activations'),
                    o.evalExpression({
                        'g.guideName': 'name',
                        'g.guideId': 'id',
                        'pageId': 'ifNull(steps.activations.pageId, steps.pageId)',
                        'featureId':
                            'ifNull(steps.activations.featureId, ifNull(steps.activations.location.featureId, ifNull(steps.featureId, steps.featureLocationId)))',
                    }),
                    o.filter('!isNil(pageId) || !isNil(featureId)'),
                    o.group(['appId', 'pageId', 'featureId'], o.groupField('guideData', { list: 'g' })),
                ),
            ),
            o.sort(['appId', '-r.appVersionTimestamp']),
            o.group(
                ['pageId', 'pageName', 'featureId', 'featureName', 'app', 'appWide', 'guideData'],
                o.groupField('eventsData', { list: 'r' }),
            ),
            o.filter('len(eventsData) > 1'),
            o.evalExpression({
                isBrokenPage: 'isNil(featureId)',
            }),
            o.filter('if(isBrokenPage, eventsData[0].numPageEvents==0, eventsData[0].numFeatureEvents==0)'),
            o.filter(
                'if(isBrokenPage, sum(map(eventsData, numPageEvents)) > 0, sum(map(eventsData, numFeatureEvents)) > 0)',
            ),
            o.evalExpression({
                isBrokenFeature: '!isBrokenPage && (appWide == true || eventsData[0].numPageEvents > 0)',
                percentVisitors:
                    'if(isBrokenPage,100*sum(map(eventsData, numPageVisitors))/app.totalVisitors ,100*sum(map(eventsData, numFeatureVisitors))/app.totalVisitors)',
            }),
            o.filter('(isBrokenFeature || isBrokenPage) && percentVisitors > 1.0'),
            o.evalExpression({
                isBrokenCount: '(isBrokenPage && !isBrokenFeature) || (isBrokenFeature && !isBrokenPage)',
                currentProdVersion: 'eventsData[0].appVersion',
                currentProductionVersionEvents:
                    'if(isBrokenPage, eventsData[0].numPageEvents, eventsData[0].numFeatureEvents)',
                currentProductionVersionVisitors:
                    'if(isBrokenPage, eventsData[0].numPageVisitors, eventsData[0].numFeatureVisitors)',
            }),
            o.reduce({
                totalBroken: {
                    countIf: {
                        if: 'isBrokenCount',
                    },
                },
            }),
        ),
};
